import { useState, useEffect, useRef, useCallback } from "react";
import { Upload, Loader2, FileSpreadsheet, AlertTriangle, CheckCircle2, ArrowLeft, ArrowRight, History, Undo2, Save } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { ImportBadge } from "../../components/admin/ImportBadge";
import { formatGBP } from "../../components/common/StockBadge";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";

const selectCls = "bg-black/30 border border-white/15 text-white rounded-md px-2 py-1.5 text-sm";
const STATUS_FILTERS = ["ALL", "NEW", "EXACT_MATCH", "PROBABLE_MATCH", "CONFLICT", "DUPLICATE_IN_FILE", "INVALID"];

function Stat({ label, value, tone }) {
  return (
    <GlassCard className="p-4" hover={false} data-testid={`import-stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <p className={`font-playfair text-2xl ${tone || "text-white"}`}>{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </GlassCard>
  );
}

function PriceCell({ pp }) {
  if (!pp) return <span className="text-white/40 text-xs">—</span>;
  const tone = pp.retail_status === "no_change" ? "text-white/50"
    : pp.retail_status === "retail_increased" ? "text-firered"
    : pp.retail_status === "retail_decreased" ? "text-emerald-400"
    : pp.retail_status === "new_price" ? "text-sky-400" : "text-amber";
  return (
    <div className="text-xs">
      <span className="text-white/60">{pp.existing_retail != null ? formatGBP(pp.existing_retail) : "—"}</span>
      <span className="text-white/30 mx-1">→</span>
      <span className={tone}>{pp.imported_retail != null ? formatGBP(pp.imported_retail) : "—"}</span>
      {pp.retail_pct != null && pp.retail_pct !== 0 && (
        <span className={`ml-1 ${pp.significant ? "font-bold " : ""}${tone}`} data-testid="price-delta">
          ({pp.retail_pct > 0 ? "+" : ""}{pp.retail_pct}%){pp.significant ? " ⚑" : ""}
        </span>
      )}
    </div>
  );
}

export default function AdminImport() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [stage, setStage] = useState("upload"); // upload | mapping | review | results
  const [supplierId, setSupplierId] = useState("emperor");
  const [suppliers, setSuppliers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [targetFields, setTargetFields] = useState({});
  const [detected, setDetected] = useState(null); // {filename, sheets}
  const [file, setFile] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [mapping, setMapping] = useState({});
  const [templateId, setTemplateId] = useState("");
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [sheetFilter, setSheetFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [catMap, setCatMap] = useState({});
  const [includeStatuses, setIncludeStatuses] = useState({ NEW: true, EXACT_MATCH: true });
  const [pricePolicy, setPricePolicy] = useState("if_empty");
  const [rollingBack, setRollingBack] = useState(null);
  const fileRef = useRef(null);

  const loadHistory = useCallback(() => { api.get("/admin/imports").then(({ data }) => setHistory(data)).catch(() => {}); }, []);
  useEffect(() => {
    loadHistory();
    api.get("/admin/suppliers").then(({ data }) => setSuppliers(data)).catch(() => {});
    api.get("/admin/imports-meta/target-fields").then(({ data }) => setTargetFields(data)).catch(() => {});
  }, [loadHistory]);
  useEffect(() => { api.get("/admin/import-templates", { params: { supplier_id: supplierId } }).then(({ data }) => setTemplates(data)).catch(() => {}); }, [supplierId]);

  const onChooseFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setLoading(true);
    try {
      const form = new FormData(); form.append("file", f);
      const { data } = await api.post("/admin/imports/detect", form, { headers: { "Content-Type": "multipart/form-data" } });
      setDetected(data);
      const first = data.sheets[0];
      setSheetName(first?.name || "");
      autoMap(first);
      setStage("mapping");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not read workbook");
    } finally { setLoading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const autoMap = (sheet) => {
    if (!sheet) return;
    const low = sheet.header.map((h) => h.trim().toLowerCase());
    const guess = {};
    const aliases = {
      item: ["item no.", "item no", "sku", "item code"], code: ["supplier code", "product code"],
      barcode: ["barcode", "ean", "upc"], name: ["product name", "name"], cat: ["category"],
      desc: ["description"], shots: ["shots"], rsp: ["rsp"], cost: ["cost inc vat"], video: ["video link"],
    };
    Object.entries(aliases).forEach(([k, opts]) => {
      const hit = opts.find((o) => low.includes(o));
      if (hit) guess[k] = sheet.header[low.indexOf(hit)];
    });
    setMapping(guess);
  };

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.template_id === id);
    if (t) { setMapping(t.mappings || {}); if (t.sheet_name) setSheetName(t.sheet_name); }
  };

  const saveTemplate = async () => {
    const name = window.prompt("Template name:", detected?.filename?.replace(/\.[^.]+$/, "") || "Template");
    if (!name) return;
    try {
      await api.post("/admin/import-templates", { supplier_id: supplierId, name, sheet_name: sheetName, mappings: mapping });
      toast.success("Mapping template saved");
      const { data } = await api.get("/admin/import-templates", { params: { supplier_id: supplierId } });
      setTemplates(data);
    } catch { toast.error("Could not save template"); }
  };

  const runPreview = async () => {
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("supplier_id", supplierId);
      form.append("mapping", JSON.stringify(mapping));
      if (sheetName) form.append("sheet_name", sheetName);
      if (templateId) form.append("template_id", templateId);
      const { data } = await api.post("/admin/imports/preview", form, { headers: { "Content-Type": "multipart/form-data" } });
      setBatch(data); setCatMap(data.category_map || {}); setStage("review");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Preview failed");
    } finally { setLoading(false); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const include = Object.keys(includeStatuses).filter((k) => includeStatuses[k]);
      const { data } = await api.post(`/admin/imports/${batch.import_id}/commit`, { include_statuses: include, price_policy: pricePolicy, category_map: catMap });
      setResult(data); setStage("results"); loadHistory();
      toast.success(`Import committed: ${data.created} created, ${data.updated} updated`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Commit failed");
    } finally { setCommitting(false); }
  };

  const rollback = async (h) => {
    if (!window.confirm(`Roll back import "${h.filename}"? Updated products are restored to prior values; created products are removed (or deactivated if referenced).`)) return;
    setRollingBack(h.import_id);
    try {
      const { data } = await api.post(`/admin/imports/${h.import_id}/rollback`);
      toast.success(`Rollback ${data.status}: ${data.result.restored} restored, ${data.result.removed} removed, ${data.result.deactivated} deactivated`);
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Rollback failed");
    } finally { setRollingBack(null); }
  };

  const reset = () => { setStage("upload"); setBatch(null); setResult(null); setDetected(null); setFile(null); setTemplateId(""); setMapping({}); setSheetFilter("ALL"); setStatusFilter("ALL"); };

  // ---- UPLOAD ----
  if (stage === "upload") {
    return (
      <div className="space-y-6" data-testid="admin-import">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl text-white">Product Import</h1>
          <p className="mt-2 text-muted-foreground">Select a supplier, upload a workbook, map columns, then run a dry-run preview. Nothing is saved until you confirm.</p>
        </div>
        <GlassCard className="p-8" hover={false}>
          <Label className="text-white/80 text-sm">Supplier</Label>
          <select data-testid="import-supplier-select" className={`${selectCls} block w-full max-w-sm mt-1 mb-6`} value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            {suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
          </select>
          <div className="text-center border-t border-white/10 pt-6">
            <FileSpreadsheet className="h-12 w-12 text-gold mx-auto" />
            <p className="mt-4 text-white font-medium">Upload supplier spreadsheet</p>
            <p className="text-sm text-muted-foreground mt-1">.xls / .xlsx. Multi-sheet workbooks supported.</p>
            <input ref={fileRef} type="file" accept=".xls,.xlsx" hidden onChange={onChooseFile} data-testid="import-file-input" />
            <Button data-testid="import-upload-btn" disabled={loading} onClick={() => fileRef.current?.click()} className="cf-gold-btn font-semibold gap-2 mt-6">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {loading ? "Reading…" : "Choose Workbook"}
            </Button>
          </div>
        </GlassCard>

        <div>
          <h2 className="font-playfair text-lg text-white mb-3 flex items-center gap-2"><History className="h-4 w-4 text-gold" /> Import History & Audit Log</h2>
          <GlassCard className="p-0 overflow-hidden" hover={false}>
            {history.length === 0 ? <div className="p-6 text-center text-muted-foreground text-sm">No imports yet.</div> : (
              <Table>
                <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/70">File</TableHead><TableHead className="text-white/70">When</TableHead>
                  <TableHead className="text-white/70">Rows</TableHead><TableHead className="text-white/70">Status</TableHead>
                  <TableHead className="text-white/70">Result</TableHead><TableHead className="text-white/70 text-right">Rollback</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.import_id} className="border-white/10" data-testid={`import-history-${h.import_id}`}>
                      <TableCell className="text-white">{h.filename}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{new Date(h.created_at).toLocaleString("en-GB")}</TableCell>
                      <TableCell className="text-muted-foreground">{h.stats?.total_rows}</TableCell>
                      <TableCell className={h.status === "committed" ? "text-emerald-400" : "text-amber"}>{h.status}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{h.commit_result ? `+${h.commit_result.created} / ~${h.commit_result.updated}` : "—"}</TableCell>
                      <TableCell className="text-right">
                        {h.status === "committed" && h.rollback_status !== "rolled_back" && h.rollback_status !== "partially_rolled_back" ? (
                          isSuper ? (
                            <button onClick={() => rollback(h)} disabled={rollingBack === h.import_id} data-testid={`rollback-${h.import_id}`} className="inline-flex items-center gap-1 text-xs text-amber hover:text-gold">
                              {rollingBack === h.import_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />} Roll back
                            </button>
                          ) : <span className="text-xs text-white/30">Super admin</span>
                        ) : <span className="text-xs text-white/40 capitalize">{(h.rollback_status || "—").replace(/_/g, " ")}</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </GlassCard>
        </div>
      </div>
    );
  }

  // ---- MAPPING ----
  if (stage === "mapping") {
    const sheet = detected?.sheets.find((s) => s.name === sheetName) || detected?.sheets[0];
    const sample = (col) => {
      const i = sheet.header.indexOf(col);
      return i >= 0 ? (sheet.samples.map((r) => r[i]).find((v) => v) || "") : "";
    };
    return (
      <div className="space-y-6" data-testid="import-mapping">
        <button onClick={reset} className="flex items-center gap-2 text-white/70 hover:text-white" data-testid="mapping-back-btn"><ArrowLeft className="h-4 w-4" /> Start over</button>
        <h1 className="font-playfair text-2xl sm:text-3xl text-white">Map Columns</h1>
        <div className="flex flex-wrap items-center gap-4">
          {detected.sheets.length > 1 && (
            <div className="flex items-center gap-2 text-sm text-white/80">Sheet:
              <select className={selectCls} data-testid="mapping-sheet-select" value={sheetName} onChange={(e) => { setSheetName(e.target.value); autoMap(detected.sheets.find((s) => s.name === e.target.value)); }}>
                {detected.sheets.map((s) => <option key={s.name} value={s.name}>{s.name} ({s.row_count})</option>)}
              </select>
            </div>
          )}
          {templates.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-white/80">Template:
              <select className={selectCls} data-testid="mapping-template-select" value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
                <option value="">— none —</option>
                {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={saveTemplate} data-testid="save-template-btn" className="border-white/20 text-white gap-1"><Save className="h-3.5 w-3.5" /> Save mapping template</Button>
        </div>

        <GlassCard className="p-0 overflow-hidden" hover={false}>
          <Table>
            <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-white/70">System field</TableHead>
              <TableHead className="text-white/70">Supplier column</TableHead>
              <TableHead className="text-white/70">Sample value</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {Object.entries(targetFields).map(([key, label]) => (
                <TableRow key={key} className="border-white/10" data-testid={`map-row-${key}`}>
                  <TableCell className="text-white text-sm">{label}{["item", "name"].includes(key) && <span className="text-firered ml-1">*</span>}</TableCell>
                  <TableCell>
                    <select className={`${selectCls} w-full`} data-testid={`map-select-${key}`} value={mapping[key] || ""} onChange={(e) => setMapping({ ...mapping, [key]: e.target.value || undefined })}>
                      <option value="">— ignore —</option>
                      {sheet.header.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs truncate max-w-[220px]">{mapping[key] ? String(sample(mapping[key])) : ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </GlassCard>

        <div className="flex justify-end gap-3 pr-28">
          <Button variant="ghost" onClick={reset} className="text-white/70">Cancel</Button>
          <Button data-testid="run-preview-btn" onClick={runPreview} disabled={loading || !mapping.item || !mapping.name} className="cf-gold-btn font-semibold gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Run Dry-Run Preview
          </Button>
        </div>
      </div>
    );
  }

  // ---- RESULTS ----
  if (stage === "results") {
    return (
      <div className="space-y-6" data-testid="import-results">
        <h1 className="font-playfair text-2xl sm:text-3xl text-white">Import Complete</h1>
        <GlassCard className="p-8 text-center" hover={false}>
          <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
          <div className="mt-4 grid grid-cols-3 gap-4 max-w-md mx-auto">
            <div><p className="font-playfair text-3xl text-emerald-400">{result.created}</p><p className="text-xs text-muted-foreground">Created</p></div>
            <div><p className="font-playfair text-3xl text-sky-400">{result.updated}</p><p className="text-xs text-muted-foreground">Updated</p></div>
            <div><p className="font-playfair text-3xl text-white/50">{result.skipped}</p><p className="text-xs text-muted-foreground">Skipped</p></div>
          </div>
          <Button data-testid="import-done-btn" onClick={reset} className="cf-gold-btn font-semibold mt-8">New Import</Button>
        </GlassCard>
      </div>
    );
  }

  // ---- REVIEW ----
  const s = batch.stats;
  const rows = batch.rows.filter((r) =>
    (sheetFilter === "ALL" || r.source_sheet === sheetFilter) &&
    (statusFilter === "ALL" || r.status === statusFilter));

  return (
    <div className="space-y-6" data-testid="import-review">
      <div className="flex items-center justify-between gap-4">
        <button onClick={reset} className="flex items-center gap-2 text-white/70 hover:text-white" data-testid="import-back-btn"><ArrowLeft className="h-4 w-4" /> New upload</button>
        <p className="text-sm text-muted-foreground">{batch.filename}</p>
      </div>
      <h1 className="font-playfair text-2xl sm:text-3xl text-white">Preview & Review</h1>

      {batch.duplicate_warning && (
        <div className="flex items-center gap-2 rounded-lg border border-amber/30 bg-amber/10 px-4 py-3 text-amber text-sm" data-testid="duplicate-hash-warning">
          <AlertTriangle className="h-4 w-4" /> This exact file has been imported before (batch {batch.duplicate_of}). Committing again may update existing products.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Stat label="Total Rows" value={s.total_rows} />
        <Stat label="New" value={s.new} tone="text-emerald-400" />
        <Stat label="Exact Match" value={s.exact_match} tone="text-sky-400" />
        <Stat label="Conflicts" value={s.conflict} tone="text-firered" />
        <Stat label="Invalid" value={s.invalid} tone="text-white/50" />
        <Stat label="Price Changes" value={s.significant_price_changes} tone="text-amber" />
      </div>

      {/* Not present in latest file */}
      {batch.not_present?.length > 0 && (
        <GlassCard className="p-5" hover={false} data-testid="not-present-report">
          <h3 className="font-playfair text-lg text-white mb-1 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber" /> Not present in latest supplier file ({batch.not_present.length})</h3>
          <p className="text-xs text-muted-foreground mb-3">These products are linked to this supplier but were not in the uploaded file. No action is taken automatically — nothing is deleted or deactivated.</p>
          <div className="max-h-52 overflow-y-auto rounded-lg border border-white/10">
            <Table>
              <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/70">Product</TableHead><TableHead className="text-white/70">Item No.</TableHead>
                <TableHead className="text-white/70">Active</TableHead><TableHead className="text-white/70">Media</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {batch.not_present.slice(0, 100).map((p) => (
                  <TableRow key={p.product_id} className="border-white/10" data-testid={`not-present-${p.product_id}`}>
                    <TableCell className="text-white text-sm">{p.name}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{p.supplier_item_no}</TableCell>
                    <TableCell className={p.is_active ? "text-emerald-400 text-xs" : "text-white/40 text-xs"}>{p.is_active ? "Yes" : "No"}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{p.has_media ? "Yes" : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </GlassCard>
      )}

      {/* Category mapping */}
      <GlassCard className="p-5" hover={false} data-testid="category-mapping">
        <h3 className="font-playfair text-lg text-white mb-3">Category Mapping</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.keys(catMap).map((raw) => (
            <div key={raw} className="flex items-center gap-2 text-sm" data-testid={`catmap-${raw}`}>
              <span className="text-white/70 flex-1 truncate">{raw}</span>
              <span className="text-white/30">→</span>
              <input className={`${selectCls} flex-1`} value={catMap[raw] === "__MANUAL_REVIEW__" ? "" : (catMap[raw] || "")}
                placeholder={catMap[raw] === "__MANUAL_REVIEW__" ? "Manual review" : "Public category"}
                onChange={(e) => setCatMap({ ...catMap, [raw]: e.target.value })} />
            </div>
          ))}
        </div>
      </GlassCard>

      {/* Commit options */}
      <GlassCard className="p-5 flex flex-wrap items-center gap-6" hover={false} data-testid="commit-options">
        <label className="flex items-center gap-2 text-sm text-white/80"><input type="checkbox" data-testid="include-new" checked={includeStatuses.NEW} onChange={(e) => setIncludeStatuses({ ...includeStatuses, NEW: e.target.checked })} /> Import new products ({s.new})</label>
        <label className="flex items-center gap-2 text-sm text-white/80"><input type="checkbox" data-testid="include-exact" checked={includeStatuses.EXACT_MATCH} onChange={(e) => setIncludeStatuses({ ...includeStatuses, EXACT_MATCH: e.target.checked })} /> Update exact matches ({s.exact_match})</label>
        <div className="flex items-center gap-2 text-sm text-white/80">
          <span>Retail price:</span>
          <select className={selectCls} data-testid="price-policy" value={pricePolicy} onChange={(e) => setPricePolicy(e.target.value)}>
            <option value="if_empty">Set from RSP only if empty</option>
            <option value="always">Always overwrite with RSP</option>
            <option value="never">Never change</option>
          </select>
        </div>
        <p className="text-xs text-white/40 w-full">Conflicts and invalid rows are never committed automatically. Merchant fields (slug, SEO, featured, images, custom price) are protected.</p>
      </GlassCard>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select className={selectCls} data-testid="filter-sheet" value={sheetFilter} onChange={(e) => setSheetFilter(e.target.value)}>
          <option value="ALL">All sheets</option>
          {Object.keys(s.by_sheet).map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <select className={selectCls} data-testid="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          {STATUS_FILTERS.map((f) => <option key={f} value={f}>{f === "ALL" ? "All statuses" : f}</option>)}
        </select>
        <span className="text-sm text-muted-foreground">{rows.length} rows shown</span>
      </div>

      <GlassCard className="p-0 overflow-hidden" hover={false}>
        <div className="max-h-[560px] overflow-y-auto">
          <Table>
            <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-white/70">Status</TableHead><TableHead className="text-white/70">Item No.</TableHead>
              <TableHead className="text-white/70">Product</TableHead><TableHead className="text-white/70">Category</TableHead>
              <TableHead className="text-white/70">Price change</TableHead><TableHead className="text-white/70">Notes</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.row_id} className="border-white/10" data-testid={`import-row-${r.row_id}`}>
                  <TableCell><ImportBadge status={r.status} /></TableCell>
                  <TableCell className="text-white/80 text-xs">{r.supplier_item_no}</TableCell>
                  <TableCell className="text-white text-sm">{r.supplier_product_name}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{r.supplier_category_raw}{r.mapped_category && r.mapped_category !== "__MANUAL_REVIEW__" ? ` → ${r.mapped_category}` : ""}</TableCell>
                  <TableCell><PriceCell pp={r.price_preview} /></TableCell>
                  <TableCell className="text-xs">
                    {r.reason && <span className="text-white/50">{r.reason}</span>}
                    {r.warnings?.length > 0 && <span className="block text-amber mt-0.5">⚠ {r.warnings.join("; ")}</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </GlassCard>

      <div className="flex justify-end gap-3 sticky bottom-0 py-3 pr-28">
        <Button variant="ghost" onClick={reset} className="text-white/70">Cancel</Button>
        <Button data-testid="import-commit-btn" onClick={commit} disabled={committing} className="cf-gold-btn font-semibold gap-2">
          {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirm & Commit
        </Button>
      </div>
    </div>
  );
}
